import {
  AppShell,
  Button,
  Group,
  Loader,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { reset, initialize, updateField } from "../slices/form/activityTab";
import {
  SaveActivityRequest,
  useGetVentQuery,
  useSaveActivityMutation,
} from "../slices/api/ventsApi";
import { useEffect } from "react";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { IsBlockNav, SetBlockNav } from "../App";
import { isNumberInputBlank } from "../shared/utility";
import { useLocation } from "wouter";
import { LinkAnchorToVentHome } from "../shared/components/LinkAnchors";
import { LabelWithTooltipHelp } from "../shared/components/LabelWithTooltipHelp";

interface Props {
  ventId: string;
  activityId: string;
  setTabDescription: (name: string) => void;
  setEventTabDescription: (name: string) => void;
}

const ActivityTab = (props: Props) => {
  const { ventId, activityId } = props;
  const [_, navigate] = useLocation();
  const { data: activity, hasChanges } = useAppSelector(
    (state: RootState) => state.activityTab,
  );
  const {
    data: vent,
    isFetching,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({ id: ventId });
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (vent) {
      const activity = vent.activities[activityId];
      if (activity == null) {
        displayErrorMessage(
          "The activity specified in the URL does not exist.",
        );
        navigate("/");
        return;
      }
      dispatch(initialize(activity));
      props.setTabDescription(activity.description!);
      props.setEventTabDescription(vent.name);
    }
  }, [vent]);
  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);

  const [saveActivity, { isLoading: isSavingActivity }] =
    useSaveActivityMutation();

  const handleSave = async () => {
    if (activity === undefined) {
      console.error("activity is undefined but should not be");
      return;
    }
    if (activity.description === "") {
      displayErrorMessage("Description is required.");
      return;
    }
    if (isNumberInputBlank(activity.limitPerGuest)) {
      displayErrorMessage("Limit per guest is required.");
      return;
    }

    const request: SaveActivityRequest = {
      activityId: activityId,
      activity: {
        description: activity.description,
        limitPerGuest: activity.limitPerGuest,
        limitPerEvent: activity.limitPerEvent,
        hostManaged: activity.hostManaged,
      },
      ventId: ventId,
      updateTime: vent?.updateTime!,
    };

    try {
      await saveActivity(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };
  if (isErrorFetchingVent) {
    handleFetchingError(ventError);
    return null;
  }
  if (isFetching || !activity || !vent) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  return (
    <>
      <AppShell.Main>
        <Group justify="space-between" mb="xs" gap="0">
          <Stack gap="0">
            <Text mb="0" size="xl">
              {`Activity: ${activity.description}`}
            </Text>
            <LinkAnchorToVentHome ventId={ventId}>
              {vent.name}
            </LinkAnchorToVentHome>
          </Stack>
        </Group>
        <TextInput
          style={{ width: "300px" }}
          maxLength={100}
          required
          mt="md"
          label="Description"
          value={activity.description ?? ""}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "description",
                value: event.currentTarget.value,
              }),
            );
          }}
        />
        <NumberInput
          style={{ width: "200px" }}
          min={0}
          maxLength={6}
          mt="md"
          wrapperProps={{ style: { pointerEvents: "auto" } }}
          labelProps={{ style: { pointerEvents: "auto" } }}
          styles={{
            label: { pointerEvents: "auto" },
          }}
          label={
            <LabelWithTooltipHelp
              helpText="Set the limit per guest to 0 or blank if some guests are already assigned to this activity, and you want to prevent new guests from signing up for this activity."
              labelText="Limit per guest"
            />
          }
          allowNegative={false}
          allowDecimal={false}
          value={activity.limitPerGuest}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "limitPerGuest",
                value: value === "" ? 0 : value,
              }),
            );
          }}
        />
        <NumberInput
          style={{ width: "200px" }}
          min={0}
          maxLength={6}
          mt="md"
          allowNegative={false}
          allowDecimal={false}
          label="Limit per event"
          description="Set to 0 or blank for no limit, otherwise the value is a hard limit per event."
          value={activity.limitPerEvent}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "limitPerEvent",
                value: value === "" ? 0 : value,
              }),
            );
          }}
        />
        <Switch
          label="Host Managed (guests cannot update assignments for this activity)"
          mt="md"
          checked={activity.hostManaged}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "hostManaged",
                value: event.currentTarget.checked,
              }),
            )
          }
        />
        <div>
          <Button
            mt="xs"
            mr="md"
            onClick={() => dispatch(reset())}
            disabled={!hasChanges}
          >
            Discard Changes
          </Button>
          <Button
            mt="xs"
            onClick={handleSave}
            disabled={!hasChanges || isSavingActivity}
            rightSection={isSavingActivity ? <Loader size={14} /> : null}
          >
            Save Activity
          </Button>
        </div>
      </AppShell.Main>
    </>
  );
};

export default ActivityTab;
